import { useCallback, useEffect, useState } from "react";

/**
 * Chat-First editor arrangement (handoff 2026-07-15).
 *
 * Two layouts of the same primitives (document + Ask Lilia):
 * - `chat` (Primary, default) — conversation is the main column; document
 *   lives in a resizable artifact pane beside it ("present as you go").
 * - `side` (classic) — document is primary; Ask Lilia docks right at 322px.
 *
 * Artifact width is clamped so the chat column never shrinks below CHAT_MIN
 * and the pane never forces horizontal overflow.
 *
 * The width is remembered per *slot* — what the two panes are holding — rather
 * than once for the pane. Reading a PDF beside the editor and reading a PDF
 * beside the conversation are different questions, and one number cannot answer
 * both: dragging to read the PDF used to follow you back into writing, and the
 * width chosen for writing used to follow you into reading.
 */

export type EditorArrangement = "chat" | "side";
export type ArtifactState = "open" | "collapsed";

const LAYOUT_KEY = "lilia-editor-arrangement";
const ARTIFACT_STATE_KEY = "lilia-artifact-state";
/**
 * What the two panes are holding. The right pane is the one being sized; the
 * slot names what is on either side of the handle.
 *
 * - document — the editor is the right pane, the conversation is on the left
 * - output   — PDF or Source on the right, the conversation on the left
 * - beside   — PDF or Source on the right, the editor on the left
 *
 * There is no fourth: the editor is in at most one pane, so it can never be on
 * both sides, and a pane is never empty.
 */
/**
 * A slot is whatever the caller says identifies this arrangement.
 *
 * It used to be one of three known names. Both panes can now hold any of four
 * surfaces, so the arrangement is a pair and the caller — which is the only
 * thing that knows the pair — supplies both the key and the width it should
 * start at. See lib/workspacePanes.widthKeyFor.
 */
export type ArtifactWidthSlot = string;

/** The original single key. Still the document slot's, so nobody's saved width is lost. */
const ARTIFACT_WIDTH_KEY = "lilia-artifact-width";
/**
 * Stored as a fraction of the space the two panes share, not as pixels.
 *
 * A pixel width is only 50% at one window size. Stored as a ratio the split
 * stays where it was put when the window resizes, and when the side panel opens
 * both panes give up a little rather than the left one absorbing all of it.
 *
 * The old pixel keys are left where they are and no longer read. Converting
 * them would carry a proportion nobody chose — they were written against a
 * default of 560px, and the default is half the window now.
 */
const RATIO_KEY = (slot: ArtifactWidthSlot) => `lilia-artifact-ratio:${slot}`;

/** Half each. The whole point. */
export const DEFAULT_SPLIT = 0.5;
/** Neither pane may be squeezed past this fraction by a drag. */
const MIN_RATIO = 0.15;
const MAX_RATIO = 0.85;

export const CHAT_MIN = 300;
export const ARTIFACT_MIN = 320;
export const ARTIFACT_MAX = 920;
export const ARTIFACT_DEFAULT = 560;
/**
 * Where the output pane starts once the editor has moved beside it.
 *
 * Wider than the default, because at that point the right pane is the thing
 * being read rather than the thing being glanced at. Inherited from Split,
 * which is where the case first came up; the case outlived it.
 */
export const ARTIFACT_BESIDE_DEFAULT = 760;
/** Side layout's docked Ask Lilia width — parity with existing dock. */
export const SIDE_AI_WIDTH = 322;
/** Thin re-open rail when artifact or side-AI is collapsed. */
export const REOPEN_RAIL_W = 34;
/** Left utility rail width (PanelRail). */
export const LEFT_RAIL_W = 52;
/** Approximate left panel width when a PanelRail tab is open. */
export const LEFT_PANEL_W = 258;
/** Resize handle between chat and artifact. */
export const RESIZE_HANDLE_W = 7;

function readLayout(): EditorArrangement {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    if (v === "side" || v === "chat") return v;
  } catch { /* ignore */ }
  // Primary (chat) is the default product arrangement (2026-07-19).
  // Persisted preference still wins when set.
  return "chat";
}

function readArtifactState(): ArtifactState {
  try {
    const v = localStorage.getItem(ARTIFACT_STATE_KEY);
    if (v === "collapsed" || v === "open") return v;
  } catch { /* ignore */ }
  return "open";
}

function readWidthKey(key: string): number | null {
  try {
    const n = Number(localStorage.getItem(key));
    if (Number.isFinite(n) && n >= ARTIFACT_MIN && n <= ARTIFACT_MAX) return n;
  } catch { /* ignore */ }
  return null;
}

/**
 * The split for one arrangement: what was stored, else half.
 *
 * Read lazily rather than all at once, because the set of arrangements is open
 * — twelve orderings of four surfaces, most of which nobody will ever visit and
 * none of which should cost a localStorage read until they do.
 */
function readSlotRatio(slot: ArtifactWidthSlot): number {
  try {
    const n = Number(localStorage.getItem(RATIO_KEY(slot)));
    if (Number.isFinite(n) && n >= MIN_RATIO && n <= MAX_RATIO) return n;
  } catch { /* ignore */ }
  return DEFAULT_SPLIT;
}

/** The width the two panes divide between them, handle included. */
export function splittableWidth(opts: {
  viewportWidth: number;
  leftPanelOpen?: boolean;
}): number {
  const chrome = LEFT_RAIL_W + (opts.leftPanelOpen ? LEFT_PANEL_W : 0) + RESIZE_HANDLE_W;
  return Math.max(0, opts.viewportWidth - chrome);
}

/** Max artifact width so chat keeps CHAT_MIN and we never overflow. */
export function maxArtifactWidth(opts: {
  viewportWidth: number;
  leftPanelOpen?: boolean;
}): number {
  const left = LEFT_RAIL_W + (opts.leftPanelOpen ? LEFT_PANEL_W : 0) + RESIZE_HANDLE_W;
  const available = opts.viewportWidth - left - CHAT_MIN;
  return Math.max(ARTIFACT_MIN, Math.min(ARTIFACT_MAX, available));
}

export function useChatFirstLayout(opts?: { leftPanelOpen?: boolean }) {
  const leftPanelOpen = opts?.leftPanelOpen ?? false;
  const [layout, setLayoutRaw] = useState<EditorArrangement>(readLayout);
  const [artifact, setArtifactRaw] = useState<ArtifactState>(readArtifactState);
  /** The split for each arrangement visited so far, as a fraction. */
  const [ratios, setRatios] = useState<Record<ArtifactWidthSlot, number>>({});
  /**
   * Which arrangement the workspace is in.
   *
   * Set by whoever knows — EditorPage, which owns what each pane is showing.
   * The hook cannot work it out for itself and should not try.
   */
  const [widthSlot, setWidthSlotRaw] = useState<ArtifactWidthSlot>("ask|edit");
  const setWidthSlot = useCallback((slot: ArtifactWidthSlot) => {
    setWidthSlotRaw(slot);
    setRatios((prev) => (slot in prev ? prev : { ...prev, [slot]: readSlotRatio(slot) }));
  }, []);
  const [viewportW, setViewportW] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth : 1280),
  );

  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const maxW = maxArtifactWidth({ viewportWidth: viewportW, leftPanelOpen });
  const splittable = splittableWidth({ viewportWidth: viewportW, leftPanelOpen });
  const ratio = ratios[widthSlot] ?? DEFAULT_SPLIT;
  // Half of what the panes share, then clamped so neither can be crushed.
  const clampedWidth = Math.min(
    maxW,
    Math.max(ARTIFACT_MIN, Math.round(splittable * ratio)),
  );

  const setLayout = useCallback((next: EditorArrangement) => {
    setLayoutRaw(next);
    try { localStorage.setItem(LAYOUT_KEY, next); } catch { /* ignore */ }
    // Entering chat-primary always shows the artifact (present as you go).
    if (next === "chat") {
      setArtifactRaw("open");
      try { localStorage.setItem(ARTIFACT_STATE_KEY, "open"); } catch { /* ignore */ }
    }
  }, []);

  const openArtifact = useCallback(() => {
    setArtifactRaw("open");
    try { localStorage.setItem(ARTIFACT_STATE_KEY, "open"); } catch { /* ignore */ }
  }, []);

  const closeArtifact = useCallback(() => {
    setArtifactRaw("collapsed");
    try { localStorage.setItem(ARTIFACT_STATE_KEY, "collapsed"); } catch { /* ignore */ }
  }, []);

  /**
   * Sizes the arrangement the workspace is currently in, and only that one.
   *
   * Takes pixels, because that is what a drag speaks, and stores the fraction
   * they represent.
   */
  const setArtifactWidth = useCallback((w: number | ((prev: number) => number)) => {
    setRatios((prev) => {
      const span = splittableWidth({
        viewportWidth: typeof window !== "undefined" ? window.innerWidth : viewportW,
        leftPanelOpen,
      });
      if (span <= 0) return prev;
      const current = (prev[widthSlot] ?? DEFAULT_SPLIT) * span;
      const raw = typeof w === "function" ? w(current) : w;
      const next = Math.min(MAX_RATIO, Math.max(MIN_RATIO, raw / span));
      if (next === prev[widthSlot]) return prev;
      try { localStorage.setItem(RATIO_KEY(widthSlot), next.toFixed(4)); } catch { /* ignore */ }
      return { ...prev, [widthSlot]: next };
    });
  }, [widthSlot, leftPanelOpen, viewportW]);

  /** ResizeHandle reports positive delta when the pointer moves right.
   *  Handle sits left of the artifact, so moving left (negative) grows it. */
  const onArtifactResize = useCallback((delta: number) => {
    setArtifactWidth((prev) => {
      const max = maxArtifactWidth({
        viewportWidth: typeof window !== "undefined" ? window.innerWidth : viewportW,
        leftPanelOpen,
      });
      return Math.min(max, Math.max(ARTIFACT_MIN, prev - delta));
    });
  }, [leftPanelOpen, setArtifactWidth, viewportW]);

  return {
    layout,
    setLayout,
    isChatLayout: layout === "chat",
    isSideLayout: layout === "side",
    artifact,
    artifactOpen: artifact === "open",
    openArtifact,
    closeArtifact,
    artifactWidth: clampedWidth,
    setArtifactWidth,
    onArtifactResize,
    maxArtifactWidth: maxW,
    widthSlot,
    setWidthSlot,
  };
}
